import { Fragment, useEffect, useRef, useState, type ChangeEvent, type InputHTMLAttributes } from 'react';
import { CODE_ALPHABET, CODE_GROUPS, formatCode, sanitizeCodeInput } from '@amg/protocol';
import { cn } from '../lib/cn';
import { fieldClass } from './ui/Field';

/** Spells the code for screen readers: "K 7 Q, M 2 P X, 9 R D". */
export function spokenCode(code: string): string {
  return formatCode(code)
    .split('-')
    .map((g) => g.split('').join(' '))
    .join(', ');
}

/**
 * The connection code as a "ticket": three groups of large monospace cells. The same grouping
 * appears in the code field people type into, so what they read matches what they type.
 */
export function CodeTicket({ code, size = 'lg', className }: { code: string; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const groups: string[] = [];
  let offset = 0;
  for (const n of CODE_GROUPS) {
    groups.push(code.slice(offset, offset + n));
    offset += n;
  }
  const cell =
    size === 'lg'
      ? 'h-13 w-10 text-[1.625rem] sm:h-14 sm:w-11 sm:text-[1.75rem]'
      : size === 'md'
        ? 'h-9 w-7 text-[1.0625rem]'
        : 'h-8 w-[1.375rem] text-[0.9375rem]';
  return (
    <div role="img" aria-label={`Connection code: ${spokenCode(code)}`} className={cn('flex items-center', size === 'lg' ? 'gap-2 sm:gap-2.5' : size === 'md' ? 'gap-1.5' : 'gap-1', className)}>
      {groups.map((group, gi) => (
        <Fragment key={gi}>
          {gi > 0 && (
            <span aria-hidden className={cn('text-ink-4', size === 'lg' ? 'text-xl' : 'text-sm')}>
              –
            </span>
          )}
          <span aria-hidden className={cn('flex', size === 'lg' ? 'gap-1 sm:gap-1.5' : size === 'md' ? 'gap-1' : 'gap-0.5')}>
            {group.split('').map((ch, i) => (
              <span key={i} className={cn('code-cell', cell)}>
                {ch}
              </span>
            ))}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

/** Minutes:seconds until the code expires; warns in the last two minutes. */
export function useCountdown(expiresAt: string | null | undefined): { seconds: number; label: string } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [expiresAt]);
  const seconds = expiresAt ? Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 1000)) : 0;
  const label = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return { seconds, label };
}

function countSymbols(text: string): number {
  let n = 0;
  for (const ch of text.toUpperCase()) if (CODE_ALPHABET.includes(ch) || ch === 'O' || ch === 'I' || ch === 'L') n++;
  return n;
}

/**
 * Single text field (so paste, autofill and screen readers behave) that groups the code as
 * people type and keeps the caret where they were typing.
 */
export function CodeInput({ value, onValueChange, className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & { value: string; onValueChange: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || caret.current === null) return;
    // Place the caret after the same number of symbols it followed before formatting.
    let symbols = caret.current;
    let pos = 0;
    while (pos < value.length && symbols > 0) {
      if (value[pos] !== '-') symbols--;
      pos++;
    }
    el.setSelectionRange(pos, pos);
    caret.current = null;
  }, [value]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    caret.current = countSymbols(raw.slice(0, e.target.selectionStart ?? raw.length));
    onValueChange(sanitizeCodeInput(raw));
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="text"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="characters"
      spellCheck={false}
      maxLength={16}
      value={value}
      onChange={onChange}
      className={cn(
        fieldClass,
        'h-14 text-center font-mono text-[1.375rem] font-medium uppercase tracking-[0.14em] tabular-nums placeholder:font-normal placeholder:tracking-[0.14em] sm:text-[1.5rem]',
        className,
      )}
      {...rest}
    />
  );
}
